import { Link } from "react-router-dom";
import { ArrowUpRight, Factory, FileCheck2, Ship, Package, Repeat2 } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { IMAGES } from "../lib/site";

const REASONS = [
  {
    icon: Factory,
    title: "Direct Indonesian Sourcing",
    copy: "We work directly with selected Indonesian manufacturers and producers — at the source, not through layers of intermediaries.",
  },
  {
    icon: FileCheck2,
    title: "Product Transparency",
    copy: "Product specifications and supporting documents can be provided for buyer review before you commit to an order.",
  },
  {
    icon: Ship,
    title: "Export Coordination",
    copy: "From sourcing and quotation to documentation and shipment coordination — one structured export workflow.",
  },
  {
    icon: Package,
    title: "Flexible Supply",
    copy: "Bulk orders, custom packaging and private-label requirements can be discussed based on supplier capability.",
  },
  {
    icon: Repeat2,
    title: "Long-Term Relationships",
    copy: "We focus on building repeat supply relationships rather than one-off transactions — consistency over volume promises.",
  },
];

export default function WhyHaqila() {
  return (
    <>
      <SEO />
      <div data-testid="why-page" className="bg-offwhite">
        <section className="pt-40 pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="WHY" title="Our Position" />
            <Reveal className="mt-8 max-w-4xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
                Why Work With HAQILA?
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
                Because serious sourcing is not about owning factories — it's about knowing the right ones, verifying
                the product and coordinating the supply.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 sm:pb-32">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <Stagger gap={0.1}>
              {REASONS.map((reason, i) => (
                <StaggerItem key={reason.title}>
                  <div
                    data-testid={`why-reason-${i + 1}`}
                    className="group grid md:grid-cols-12 gap-6 items-center py-10 sm:py-14 border-t border-charcoal/10 hover:bg-sand transition-colors duration-500 px-4 sm:px-8 -mx-4 sm:-mx-8"
                  >
                    <span className="md:col-span-2 font-display text-5xl sm:text-6xl font-black tracking-tight text-outline-dark group-hover:text-forest group-hover:[-webkit-text-stroke:0px] transition-colors duration-500">
                      0{i + 1}
                    </span>
                    <div className="md:col-span-4 flex items-center gap-5">
                      <reason.icon className="w-7 h-7 text-forest shrink-0" strokeWidth={1.5} />
                      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{reason.title}</h2>
                    </div>
                    <p className="md:col-span-6 text-base text-charcoal/65 leading-relaxed">{reason.copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            <Reveal className="mt-20 relative overflow-hidden bg-charcoal text-offwhite">
              <img
                src={IMAGES.logistics}
                alt="Container port — export logistics"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover img-spotlight opacity-25"
              />
              <div className="relative z-10 p-10 sm:p-16 lg:p-20 max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-6">The HAQILA Difference</p>
                <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                  International buyers manage their Indonesian sourcing through one export partner.
                </p>
                <Link
                  to="/request-a-quote"
                  data-testid="why-quote-cta"
                  className="group mt-10 inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
                >
                  Start Your Sourcing Inquiry
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
